<template>
  <div>
    <loading ref="loading"></loading>
    <v-card class="elevation-0" tile style="min-height: 100vh">
      <v-toolbar dark color="primary">
        <v-toolbar-title
        >
          <v-icon class="mr-2" color="primary lighten-1">
            mdi-cash-clock
          </v-icon>
          <span>Limit Order</span>
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon dark @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>
      <v-card-text>
        <v-form
          ref="form"
          class="mb-0 pb-0"
          lazy-validation
          v-model="formValid"
        >
          <v-row>
            <v-col cols="12" class="pr-4" md="7">
              <p class="text-subtitle-1 font-weight-bold pt-2 pb-2">
                Create Limit Order
              </p>
              <v-simple-table dense :class="'cartItems'">
                <template v-slot:default>
                  <thead>
                  <tr>
                    <th class="text-center" width="60%">Product</th>
                    <th class="text-center" width="10%">Max Qty</th>
                    <th class="text-center" width="20%">Target Price</th>
                    <th class="text-center" width="10%">Total</th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr>
                    <td>
                      <product-info-cell
                        :product="selectedProduct"
                        :buy-sell="buySell"
                        :hide-volume-discount="true"
                      ></product-info-cell>
                    </td>
                    <td>
                      <number-field
                        :options="
                            selectedProduct.allowFractionalQty
                              ? { type: `fractionalQty` }
                              : { type: `qty` }
                          "
                        :rules="
                            getRulesForItem(selectedProduct, buySell, true)
                          "
                        v-model="qty"
                        :min="getMin(selectedProduct, buySell)"
                      />
                    </td>
                    <td class="text-right">
                      <number-field
                        placeholder="e.g. 1.00"
                        :options="{ type: `dollar` }"
                        v-model="limitPrice"
                        :rules="targetPriceRules()"
                      ></number-field>
                    </td>
                    <td class="text-right">
                      {{ (limitPrice * qty) | toCurrency }}
                    </td>
                  </tr>
                  <tr v-if="buySell === `buy` && handlingFee > 0">
                    <td colspan="3">
                      <handling-tooltip></handling-tooltip>
                    </td>
                    <td class="text-right">
                      {{ handlingFee | toCurrency }}
                    </td>
                  </tr>
                  <tr>
                    <td colspan="3" class="text-right">
                      <span class="font-weight-bold">Your Total</span>
                    </td>
                    <td>
                        <span
                          class="font-weight-bold float-right text-no-wrap"
                        >{{ total | toCurrency }}</span
                        >
                    </td>
                  </tr>
                  </tbody>
                </template>
              </v-simple-table>
              <v-alert
                class="mt-4"
                border="bottom"
                colored-border
                type="warning"
                elevation="2"
                v-if="qty > getMax(selectedProduct, buySell)"
              >Please note: you are placing a limit order for more than we
                currently have in stock. Your limit order will fail to execute
                if we do not have adequate stock at the time your limit price is
                reached.
              </v-alert
              >
              <p class="text-caption mt-2">
                All orders are subject to the
                <a style="color: #2196f3" @click="showTerms"
                >Terms & Conditions</a
                >. You may only add one product per limit order. Each executed
                limit order will create a unique sales order. Executed orders
                may not be ready for immediate shipment as we sometimes pre-sell
                products on order to us. You have the ability to cancel a limit
                order in working status through PlatformGold&reg; any time
                before expiration or execution. Currently, only buy limit orders
                are supported on select products.
              </p>
            </v-col>
            <v-col cols="12" class="pl-4" md="5">
              <v-stepper v-model="detailsStep" class="elevation-0 pa-0">
                <v-stepper-items>
                  <v-stepper-content step="1" class="pt-1">
                    <payment-method
                      :buy-sell="buySell"
                      :company-id="getSelectedCompany.id"
                      v-on:loaded="
                        requiredComponentsLoaded.paymentMethod = true
                      "
                    ></payment-method>
                    <shipping-instructions
                      :buy-sell="buySell"
                      :company-id="getSelectedCompany.id"
                      v-on:loaded="
                        requiredComponentsLoaded.shippingInstructions = true
                      "
                    ></shipping-instructions>
                    <shipping-address
                      :buy-sell="buySell"
                      :company-id="getSelectedCompany.id"
                      :address-validator="validateShippingAddress"
                      v-on:addNewAddress="addNewAddress"
                      v-on:loaded="
                        requiredComponentsLoaded.shippingAddress = true
                      "
                    ></shipping-address>
                    <div class="font-weight-bold pa-0 pt-2">
                      Your Trade #
                    </div>
                    <v-text-field
                      dense
                      :placeholder="getCustomerReferencePlaceholder(buySell)"
                      :rules="[...characterLimit(35)]"
                      v-model="customerReferenceNumber"
                    ></v-text-field>
                    <div class="font-weight-bold pa-0 pt-2">Order Notes</div>
                    <v-textarea
                      outlined
                      placeholder="Notes about your order, such as special delivery instructions"
                      v-model="orderNotes"
                    ></v-textarea>
                    <div class="font-weight-bold pa-0">Limit Order Expires</div>
                    <v-menu
                      ref="dateRangeMenu"
                      v-model="dateRangeMenu"
                      :close-on-content-click="false"
                      transition="scale-transition"
                      offset-y
                      min-width="auto"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-radio-group row v-model="timeInForce">
                          <v-radio
                            :label="
                              'Next Market Close (' +
                              $options.filters.formattedDateAndTime(
                                new Date(`${nextMarketClose()} 22:00:00.000+00`)
                              ) +
                              ')'
                            "
                            value="marketClose"
                          ></v-radio>
                          <v-radio
                            :label="
                              'Choose Day (' +
                              (timeInForce === `chooseDay`
                                ? $options.filters.formattedDateAndTime(
                                    inForceExpiration()
                                  )
                                : 'Max 28 Days') +
                              ')'
                            "
                            value="chooseDay"
                            v-on="on"
                          ></v-radio>
                        </v-radio-group>
                      </template>
                      <v-date-picker
                        v-model="datePickerDate"
                        :allowed-dates="allowedDates"
                      >
                        <v-spacer></v-spacer>
                        <v-btn
                          text
                          color="primary"
                          @click="dateRangeMenu = false"
                        >
                          Cancel
                        </v-btn>
                        <v-btn text color="primary" @click="applyDate">
                          OK
                        </v-btn>
                      </v-date-picker>
                    </v-menu>
                    <trade-execution
                      :buy-sell="buySell"
                      :limit-order="true"
                      :company-id="getSelectedCompany.id"
                      :error="limitOrdersDisabledError"
                      v-on:close="close()"
                      v-on:executeTrade="executeTrade()"
                      v-if="
                        Object.values(requiredComponentsLoaded).filter(
                          (c) => !c
                        ).length === 0
                      "
                    ></trade-execution>
                  </v-stepper-content>
                  <v-stepper-content step="2">
                    <address-form
                      :selected-address="{}"
                      hide-default-shipping-checkbox="true"
                      add-to-address-book-option="true"
                      v-on:close="detailsStep = 1"
                      v-on:save="newAddressSaved()"
                    ></address-form>
                  </v-stepper-content>
                </v-stepper-items>
              </v-stepper>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import Loading from '@/components/Loading'
import productMixin from '@/mixins/product'
import orderMixin from '@/mixins/order'
import {mapGetters, mapState} from 'vuex'
import NumberField from '@/components/NumberField'
import PaymentMethod from '@/components/cart/PaymentMethod'
import ShippingInstructions from '@/components/cart/ShippingInstructions'
import ShippingAddress from '@/components/cart/ShippingAddress'
import ProductInfoCell from '@/components/cart/ProductInfoCell'
import _ from 'lodash'
import {UNITED_STATES} from '@/util/constants'
import HandlingTooltip from '@/components/cart/HandlingTooltip'
import TradeExecution from '@/components/cart/TradeExecution'
import AddressForm from '@/components/company/AddressForm'

export default {
  props: ['shown', 'selectedProduct', 'buySell', 'quantity'],
  mixins: [productMixin, orderMixin],
  components: {
    HandlingTooltip,
    ShippingAddress,
    ShippingInstructions,
    PaymentMethod,
    Loading,
    NumberField,
    ProductInfoCell,
    TradeExecution,
    AddressForm,
  },
  computed: {
    ...mapGetters([
      'getSelectedCompany',
      'getSelectedTradingRole',
      'getConfig',
      'getRecentAddress',
    ]),
    selectedShippingAddress: {
      get() {
        return this.$store.getters['getCartField'](
          `${this.buySell}.selectedShippingAddress`
        )
      },
      set(value) {
        this.$store.commit('updateCartField', {
          path: `${this.buySell}.selectedShippingAddress`,
          value,
        })
      },
    },
    ...mapState({
      spotPrices: (state) => state.user.spotPrices,
    }),
    total() {
      return this.limitPrice * this.qty + this.handlingFee
    },
    min() {
      return this.buySell === `buy`
        ? this.fixNumber(this.prices.ask * 0.75)
        : this.prices.bid
    },
    max() {
      return this.buySell === `buy`
        ? this.prices.ask
        : this.fixNumber(this.prices.bid * 1.25)
    },
    prices() {
      return this.computePrices(
        this.selectedProduct,
        this.spotPrices,
        this.getSelectedCompany,
        this.getSelectedTradingRole
      )
    },
    handlingFee() {
      const countries = Object.values(this.getConfig('COUNTRIES'))
      const selectedShippingAddress = this.$store.getters['getCartField'](
        `${this.buySell}.selectedShippingAddress`
      )
      return this.getSelectedCompany.waiveHandling
        ? 0
        : productMixin.methods.getHandlingFee(
          [{item: this.selectedProduct, quantity: this.qty}],
          _.get(selectedShippingAddress, `location.country`, UNITED_STATES),
          countries
        )
    },
    selectedShippingInstructionId: {
      get() {
        const selectedShippingInstructions = this.$store.getters['getCartField'](`${this.buySell}.selectedShippingInstructions`) || {};
        return parseInt(selectedShippingInstructions.netsuite_internalId) || null;
      }
    },
    validateShippingAddress() {
      return this.validateShippingAddressComplete(this.buySell, this.selectedShippingInstructionId)
    },
    limitOrdersDisabledError() {
      const enabled = this.getConfig('LIMIT_ORDERS_ENABLED')
      if (enabled === true || enabled === 'true') return null
      return 'Limit orders are temporarily unavailable.'
    },
  },
  mounted() {
    this.init()
  },
  data() {
    return {
      timeInForce: null,
      timeInForceExpirationDate: null,
      qty: null,
      limitPrice: null,
      formValid: null,
      customerReferenceNumber: null,
      orderNotes: null,
      dateRangeMenu: false,
      datePickerDate: null,
      targetSpot: null,
      detailsStep: 1,
      addNewAddressType: null,
      requiredComponentsLoaded: {
        shippingInstructions: false,
        shippingAddress: false,
        paymentMethod: false,
        targetSpot: false,
      },
    }
  },
  watch: {
    limitPrice: {
      handler() {
        this.fetchSpot()
      },
    },
    qty: {
      handler() {
        this.fetchSpot()
      },
    },
    shown: {
      handler() {
        this.init()
      },
    },
  },
  methods: {
    // This allows us to experiment with different dates
    showTerms() {
      window._VMA.$emit('SHOW_TERMS')
    },
    inForceExpiration() {
      const expirationDate =
        this.timeInForce === `chooseDay`
          ? this.timeInForceExpirationDate
          : this.nextMarketClose()
      return new Date(`${expirationDate} 22:00:00.000+00`)
    },
    targetPriceRules() {
      return [
        (v) =>
          parseFloat(typeof v === `string` ? v.replace(`,`, ``) : `0`) <=
          this.max || 'Max: ' + this.$options.filters.toCurrency(this.max),
        (v) =>
          parseFloat(typeof v === `string` ? v.replace(`,`, ``) : `0`) >=
          this.min || 'Min: ' + this.$options.filters.toCurrency(this.min),
      ]
    },
    async fetchSpot() {
      const payload = {
        quantity: this.qty,
        targetPrice: this.limitPrice,
        bidAsk: this.buySell === `buy` ? `ask` : `bid`,
        productId: this.selectedProduct.id,
      }
      this.targetSpot = await this.$store.dispatch(
        `fetchSpotByTargetPrice`,
        payload
      )
      this.requiredComponentsLoaded.targetSpot = true
    },
    applyDate() {
      this.$refs.dateRangeMenu.save(this.datePickerDate)
      this.timeInForceExpirationDate = this.datePickerDate
      this.dateRangeMenu = false
    },
    addNewAddress(type) {
      this.addNewAddressType = type
      this.detailsStep = 2
    },
    async newAddressSaved() {
      await this.$store.dispatch('fetchSelectedCompanyAddresses', {
        page: 1,
        itemsPerPage: 10000,
      })
      this.selectedShippingAddress = this.getRecentAddress
      this.detailsStep = 1
    },
    async init() {
      await Promise.all([
        this.$store.dispatch('fetchCountries'),
        this.$store.dispatch('fetchConfig'),
      ])
      this.buySell = this.buySell
      this.qty = this.quantity
      this.timeInForce = 'marketClose'
      this.timeInForceExpirationDate = this.nextMarketClose()
      this.limitPrice =
        this.buySell === `buy` ? this.prices.ask : this.prices.bid
    },
    close() {
      this.$emit('close')
    },
    executeTrade() {
      if (this.$refs.form.validate()) {
        window._VMA.$emit('CONFIRM', {
          title: `Confirm Limit Order`,
          message: `Are you sure you want to execute this limit order?`,
          options: {},
          callback: async (agree) => {
            if (agree) {
              this.$refs.loading.open(
                'Executing Limit Order...',
                'Please do not navigate away from this window until your order is fully executed and you receive a confirmation number.',
                {
                  color: 'primary',
                }
              )
              // Only returns new confirmation number if one not already set.
              try {
                const payload = {
                  qty: this.qty,
                  limitPrice: this.limitPrice,
                  metalSymbol: this.targetSpot.metalId,
                  targetSpotPrice: this.targetSpot.price,
                  buySell: this.buySell,
                  timeInForceExpiration: this.inForceExpiration(),
                  customerReferenceNumber: this.customerReferenceNumber,
                  orderNotes: this.orderNotes,
                  handlingFee: this.handlingFee,
                  productId: this.selectedProduct.id,
                }
                const result = await this.$store.dispatch(
                  'submitLimitOrder',
                  payload
                )
                // route to limit order detail page
                this.$emit('close')
                window._VMA.$emit('SHOW_SNACKBAR', {
                  text: `Your limit order has been successfully submitted.`,
                  color: 'success',
                })
                await this.$router.push(`/orders/limit-orders/${result.id}`)
              } catch (e) {
                window._VMA.$emit('SHOW_SNACKBAR', {
                  text: 'We were unable to submit your limit order at this time. Please contact the Trading Desk for assistance..',
                  color: 'error',
                })
              } finally {
                this.$refs.loading.close()
              }
            }
          },
        })
      }
    },
  },
}
</script>

<style>
.cartItems table {
  border: 1px solid #eaebed;
}

.cartItems table thead {
  background-color: #f7f8fa;
  color: #888e99;
  text-transform: uppercase;
}
</style>
